package one.healthium.onboarding.ui.screens

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import one.healthium.onboarding.domain.configs.TextInputScreenConfig
import one.healthium.onboarding.ui.UserDataProvider
import one.healthium.onboarding.ui.UserProfileUiModel
import one.healthium.onboarding.ui.bindPlaceholderValues
import one.healthium.ui.components.CtaButton
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.components.AppTextField
import one.healthium.ui.compose.theme.AppTheme

interface TextInputEventDelegate {
    fun onNextClicked()
}

@Composable
internal fun TextInputScreen(
    screenConfig: TextInputScreenConfig,
    profileKey: String,
    eventDelegate: TextInputEventDelegate
) {
    UserDataProvider { userState, contract ->
        AppScaffold {
            TextInputScreenContent(
                screenConfig = screenConfig,
                profileKey = profileKey,
                userState = userState,
                onValueChanged = { contract.onAdditionalTextChanged(profileKey, it) },
                onNextClicked = eventDelegate::onNextClicked,
            )
        }
    }
}

@Composable
internal fun TextInputScreenContent(
    modifier: Modifier = Modifier,
    screenConfig: TextInputScreenConfig,
    profileKey: String,
    userState: UserProfileUiModel,
    onValueChanged: (String) -> Unit,
    onNextClicked: () -> Unit,
) {
    val value = userState.additionalText[profileKey]

    Column(
        modifier = modifier
            .fillMaxSize()
            .statusBarsPadding()
            .navigationBarsPadding()
            .padding(horizontal = 24.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        AppText(
            text = screenConfig.title?.bindPlaceholderValues(userState),
            style = AppTheme.textStyles.header,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(24.dp))
        Spacer(Modifier.weight(1f))
        AppTextField(
            modifier = Modifier.fillMaxWidth(),
            value = value ?: "",
            onValueChange = onValueChanged,
            textStyle = AppTheme.textStyles.textField.copy(textAlign = TextAlign.Center),
            keyboardOptions = KeyboardOptions(
                capitalization = KeyboardCapitalization.Words,
                imeAction = ImeAction.Done,
            ),
            showKeyboardAutomaticallyIfEmpty = true,
            maxLines = 1,
        )
        Spacer(Modifier.weight(1f))
        Spacer(Modifier.height(24.dp))
        AppText(
            text = screenConfig.hint?.bindPlaceholderValues(userState),
            style = AppTheme.textStyles.subTitle,
            color = AppTheme.colors.text400,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(37.dp))
        CtaButton(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp),
            text = screenConfig.ctaTitle,
            enabled = if (screenConfig.required) value.isNullOrBlank().not() else true,
            onClick = onNextClicked,
        )
        Spacer(Modifier.height(24.dp))
        Spacer(Modifier.navigationBarsPadding().imePadding())
    }
}
